import { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation, matchPath } from 'react-router-dom';
import { Navbar } from '../components/Shared/Navbar';
import { Sidebar } from '../components/Shared/Sidebar';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { useMenuPermissions } from '../hooks/useMenuPermissions';
import { DashboardPage } from '../pages/Dashboard/DashboardPage';
import { HomePage } from '../pages/Home/HomePage';
import { ProfilesPage } from '../pages/Profiles/ProfilesPage';
import { UsersPage } from '../pages/Users/UsersPage';
import { TeamsPage } from '../pages/Teams/TeamsPage';
import { PermissionsPage } from '../pages/Permissions/PermissionsPage';
import DocumentsPage from '../pages/Documents/DocumentsPage';
import DocumentDetailsPage from '../pages/Documents/DocumentDetailsPage';
import DocumentTreeTest from '../pages/Documents/DocumentTreeTest';
import FavoritesPage from '../pages/Documents/FavoritesPage';
import DocumentVersionComparePage from '../pages/Documents/DocumentVersionComparePage';
import ChatInDocPage from '../pages/ChatInDoc/ChatInDocPage';
import { DocumentCategoriesPage } from '../pages/Admin/DocumentCategoriesPage';
import { DocumentTagsPage } from '../pages/Admin/DocumentTagsPage';
import TemplatesPage from '../pages/Templates/TemplatesPage';
import { FormsPage } from '../pages/Forms/FormsPage';
import { FormBuilderPage } from '../pages/Forms/FormBuilderPage';
import { NotificationsPage } from '../pages/Notifications/NotificationsPage';
import { SettingsPage } from '../pages/Settings/SettingsPage';
import WorkflowsPage from '../pages/Workflows/WorkflowsPage';
import { SearchPage } from '../pages/Search/SearchPage';
import { AuditPage } from '../pages/Audit/AuditPage';
import { AuditDetailPage } from '../pages/Audit/AuditDetailPage';
import JobMonitoringPage from '../pages/Admin/JobMonitoringPage';
import { SupportPage } from '../pages/Support/SupportPage';
import { PrivacyPage } from '../pages/Privacy/PrivacyPage';
import { DocumentationPage } from '../pages/Documentation/DocumentationPage';
import { ChatInDocProvider } from '../contexts/ChatInDocContext';
import MyPrivateDocumentsPage from '../pages/Documents/MyPrivateDocumentsPage';
import DocumentPublishQueuePage from '../pages/Documents/DocumentPublishQueuePage';
import TenantPrivateStoragePage from '../pages/Settings/TenantPrivateStoragePage';
import { VisitorInboxPage } from '../pages/Signature/VisitorInboxPage';
import { VisitorSignPage } from '../pages/Signature/VisitorSignPage';

function AppHomeRedirect() {
  const { getHomePage } = useMenuPermissions();
  return <Navigate to={getHomePage()} replace />;
}

const AppLayout: React.FC = () => {
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const isFullScreen = location.pathname.startsWith('/copilot');
  const visitorInboxExact = matchPath({ path: '/sign/inbox', end: true }, location.pathname) != null;
  const visitorSignDocMatch = matchPath({ path: '/sign/inbox/:documentId', end: true }, location.pathname) != null;

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  return (
    <ChatInDocProvider>
      <div className="h-dvh overflow-hidden flex flex-col bg-gray-50">
        <Navbar onOpenMobileNav={() => setMobileNavOpen(true)} />
        <div className="flex flex-1 min-h-0 overflow-hidden">
          <Sidebar
            mobileOpen={mobileNavOpen}
            onMobileClose={() => setMobileNavOpen(false)}
          />
          <main
            className={`flex-1 min-h-0 overflow-hidden ${
              isFullScreen
                ? 'flex flex-col'
                : visitorInboxExact || visitorSignDocMatch
                  ? 'flex flex-col overflow-y-auto p-4 md:p-6'
                  : 'overflow-y-auto p-4 md:p-6'
            }`}
          >
          {visitorInboxExact ? (
            <div className="flex w-full min-h-0 flex-1 flex-col">
              <VisitorInboxPage />
            </div>
          ) : visitorSignDocMatch ? (
            <div className="flex w-full min-h-0 flex-1 flex-col">
              <VisitorSignPage />
            </div>
          ) : (
          <Routes>
            <Route path="/" element={<AppHomeRedirect />} />

          {/* Home (Command & Focus) */}
          <Route path="/home" element={
            <ProtectedRoute requiredRoles={['admin', 'manager', 'editor']}>
              <HomePage />
            </ProtectedRoute>
          } />

          {/* Dashboard - Apenas admin, manager, editor */}
          <Route path="/dashboard" element={
            <ProtectedRoute requiredRoles={['admin', 'manager', 'editor']}>
              <DashboardPage />
            </ProtectedRoute>
          } />
          
          {/* Profiles - Apenas admin, manager */}
          <Route path="/profiles" element={
            <ProtectedRoute requiredRoles={['admin', 'manager']}>
              <ProfilesPage />
            </ProtectedRoute>
          } />
          
          {/* Users - Apenas admin */}
          <Route path="/users" element={
            <ProtectedRoute requiredRoles={['admin', 'manager']}>
              <UsersPage />
            </ProtectedRoute>
          } />
          
          {/* Teams - Apenas admin, manager */}
          <Route path="/teams" element={
            <ProtectedRoute requiredRoles={['admin', 'manager']}>
              <TeamsPage />
            </ProtectedRoute>
          } />
          
          {/* Permissions - Apenas admin, manager */}
          <Route path="/permissions" element={
            <ProtectedRoute requiredRoles={['admin', 'manager']}>
              <PermissionsPage />
            </ProtectedRoute>
          } />
          
          {/* Document Details - Todos exceto user básico */}
          <Route path="/documents/:documentId/details" element={
            <ProtectedRoute requiredRoles={['admin', 'manager', 'editor', 'viewer']}>
              <DocumentDetailsPage />
            </ProtectedRoute>
          } />

          {/* Document Version Compare - Todos exceto user básico */}
          <Route path="/documents/:documentId/versions/compare" element={
            <ProtectedRoute requiredRoles={['admin', 'manager', 'editor', 'viewer']}>
              <DocumentVersionComparePage />
            </ProtectedRoute>
          } />
          
          {/* Documents - Todos exceto user básico */}
          <Route path="/documents" element={
            <ProtectedRoute requiredRoles={['admin', 'manager', 'editor', 'viewer']}>
              <DocumentsPage />
            </ProtectedRoute>
          } />
          
          {/* Favorites - Todos exceto user básico */}
          <Route path="/documents/favorites" element={
            <ProtectedRoute requiredRoles={['admin', 'manager', 'editor', 'viewer']}>
              <FavoritesPage />
            </ProtectedRoute>
          } />
          
          <Route path="/documents/test" element={
            <ProtectedRoute requiredRoles={['admin', 'manager', 'editor', 'viewer']}>
              <DocumentTreeTest />
            </ProtectedRoute>
          } />
          
          {/* Copiloto - visitantes (perfil user) não têm acesso */}
          <Route path="/copilot" element={
            <ProtectedRoute requiredRoles={['admin', 'manager', 'editor', 'viewer']}>
              <ChatInDocPage />
            </ProtectedRoute>
          } />

          {/* Document Categories - Apenas admin, manager, editor */}
          <Route path="/documents/categories" element={
            <ProtectedRoute requiredRoles={['admin', 'manager', 'editor']}>
              <DocumentCategoriesPage />
            </ProtectedRoute>
          } />
          
          {/* Document Tags - Apenas admin, manager, editor */}
          <Route path="/documents/tags" element={
            <ProtectedRoute requiredRoles={['admin', 'manager', 'editor']}>
              <DocumentTagsPage />
            </ProtectedRoute>
          } />

          <Route path="/documents/private" element={
            <ProtectedRoute requiredRoles={['admin', 'manager', 'editor', 'viewer']}>
              <MyPrivateDocumentsPage />
            </ProtectedRoute>
          } />

          <Route path="/documents/publish-queue" element={
            <ProtectedRoute requiredRoles={['admin', 'editor']}>
              <DocumentPublishQueuePage />
            </ProtectedRoute>
          } />
          
          {/* Templates - Apenas admin, manager, editor */}
          <Route path="/templates" element={
            <ProtectedRoute requiredRoles={['admin', 'manager', 'editor']}>
              <TemplatesPage />
            </ProtectedRoute>
          } />

          {/* Formulários - Apenas admin, manager, editor */}
          <Route path="/forms" element={
            <ProtectedRoute requiredRoles={['admin', 'manager', 'editor']}>
              <FormsPage />
            </ProtectedRoute>
          } />
          <Route path="/forms/:formId" element={
            <ProtectedRoute requiredRoles={['admin', 'manager', 'editor']}>
              <FormBuilderPage />
            </ProtectedRoute>
          } />

          {/* Workflows - Admin e gerente (visão da aplicação) */}
          <Route path="/workflows" element={
            <ProtectedRoute requiredRoles={['admin', 'manager']}>
              <WorkflowsPage />
            </ProtectedRoute>
          } />
          
          {/* Notifications - Todos (acesso universal) */}
          <Route path="/notifications" element={<NotificationsPage />} />
          
          {/* Settings - Apenas admin */}
          <Route path="/settings" element={
            <ProtectedRoute requiredRoles={['admin']}>
              <SettingsPage />
            </ProtectedRoute>
          } />

          <Route path="/settings/private-storage" element={
            <ProtectedRoute requiredRoles={['admin', 'manager']}>
              <TenantPrivateStoragePage />
            </ProtectedRoute>
          } />
          
          {/* Search - Todos exceto user básico */}
          <Route path="/search" element={
            <ProtectedRoute requiredRoles={['admin', 'manager', 'editor', 'viewer']}>
              <SearchPage />
            </ProtectedRoute>
          } />
          

          
          {/* Audit - Apenas admin, manager, editor */}
          <Route path="/audit" element={
            <ProtectedRoute requiredRoles={['admin', 'manager', 'editor']}>
              <AuditPage />
            </ProtectedRoute>
          } />
          
          <Route path="/audit/:id" element={
            <ProtectedRoute requiredRoles={['admin', 'manager', 'editor']}>
              <AuditDetailPage />
            </ProtectedRoute>
          } />

          {/* Monitoramento de Jobs */}
          <Route path="/jobs" element={
            <ProtectedRoute requiredRoles={['admin', 'manager', 'editor', 'viewer']}>
              <JobMonitoringPage />
            </ProtectedRoute>
          } />
          
          {/* Páginas públicas */}
          <Route path="/support" element={<SupportPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/docs" element={<DocumentationPage />} />
          
          {/* Fallback - redirecionar para página inicial apropriada */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
          )}
        </main>
        </div>
      </div>
    </ChatInDocProvider>
  );
};

export default AppLayout; 